/**
 * LoginPage — com Google Sign-In
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { getApiErrorMessage } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export default function LoginPage() {
    const { login, googleAuth } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    // Callback chamado pelo Google Identity Services
    const handleGoogleCredential = useCallback(async (response) => {
        if (!response?.credential) return;
        setGoogleLoading(true);
        try {
            const u = await googleAuth(response.credential);
            toast.success(`Bem-vindo, ${u.name}!`);
            nav(u.role === "admin" ? "/admin" : "/painel");
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Não foi possível entrar com o Google."));
        } finally {
            setGoogleLoading(false);
        }
    }, [googleAuth, nav]);

    // Carregar o script do Google Identity Services
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;

        const scriptId = "google-gsi-script";
        if (document.getElementById(scriptId)) {
            // Script já carregado — inicializar diretamente
            if (window.google?.accounts?.id) {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleCredential,
                });
                window.google.accounts.id.renderButton(
                    document.getElementById("google-signin-btn"),
                    { theme: "outline", size: "large", width: "100%", locale: "pt-PT" }
                );
            }
            return;
        }

        const script = document.createElement("script");
        script.id = scriptId;
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleCredential,
            });
            window.google.accounts.id.renderButton(
                document.getElementById("google-signin-btn"),
                { theme: "outline", size: "large", width: "100%", locale: "pt-PT" }
            );
        };
        document.body.appendChild(script);
    }, [handleGoogleCredential]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(`Bem-vindo, ${u.name}!`);
            nav(u.role === "admin" ? "/admin" : "/painel");
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Não foi possível entrar."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center py-10 px-4 bg-hero-radial-light" data-testid="login-page">
            <Card className="w-full max-w-md p-7 rounded-2xl bg-card border-border">
                <div className="flex justify-center mb-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B0B10] overflow-hidden">
                        <img src={LOGO_URL} alt="MeSafa" className="h-14 w-14 object-contain" />
                    </span>
                </div>
                <h1 className="font-display text-2xl font-semibold text-center">Entrar</h1>
                <p className="text-sm text-muted-foreground text-center mt-1">Bem-vindo de volta ao MeSafa.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <Label htmlFor="login-email">Email</Label>
                        <Input
                            id="login-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email-input"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between">
                            <Label htmlFor="login-password">Palavra-passe</Label>
                            <Link
                                to="/recuperar-senha"
                                className="text-xs text-[#6B21D9] hover:underline"
                                data-testid="login-forgot-password-link"
                            >
                                Esqueci a senha?
                            </Link>
                        </div>
                        <Input
                            id="login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading || googleLoading}
                        className="w-full btn-grad-purple text-white h-11"
                        data-testid="login-submit-button"
                    >
                        {loading ? "A entrar…" : "Entrar"}
                    </Button>
                </form>

                {/* Divisor */}
                <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">ou</span>
                    </div>
                </div>

                {/* Botão Google */}
                {GOOGLE_CLIENT_ID ? (
                    <div id="google-signin-btn" className="w-full flex justify-center" />
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        disabled={googleLoading}
                        className="w-full h-11 flex items-center gap-3 border-border hover:bg-muted/50"
                        onClick={() => toast.info("Google Sign-In: configure REACT_APP_GOOGLE_CLIENT_ID nas variáveis de ambiente do Hostinger.")}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.64 9.2045C17.64 8.5663 17.5827 7.9527 17.4764 7.3636H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.2045Z" fill="#4285F4"/>
                            <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                            <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.5931 3.68182 9C3.68182 8.4069 3.78409 7.83 3.96409 7.29V4.9582H0.957275C0.347727 6.1731 0 7.5477 0 9C0 10.4523 0.347727 11.8269 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                            <path d="M9 3.5795C10.3214 3.5795 11.5077 4.0336 12.4405 4.9255L15.0218 2.3441C13.4632 0.8918 11.4259 0 9 0C5.48182 0 2.43818 2.0168 0.957275 4.9582L3.96409 7.29C4.67182 5.1627 6.65591 3.5795 9 3.5795Z" fill="#EA4335"/>
                        </svg>
                        {googleLoading ? "A entrar com Google…" : "Continuar com Google"}
                    </Button>
                )}

                <p className="mt-6 text-center text-sm text-muted-foreground">
                    Ainda não tem conta?{" "}
                    <Link to="/registo" className="text-[#6B21D9] font-medium hover:underline">
                        Crie grátis
                    </Link>
                </p>
            </Card>
        </div>
    );
}
